import { PLACEHOLDER_IMAGE_AVATAR, thunkActionLoading } from '@configs'
import {
  addPostToLibraryAction,
  getLibraryAction,
  RootState,
  useAppDispatch,
  useAppSelector,
} from '@redux'
import { Checkbox, message, TableProps, Tooltip } from 'antd'
import moment from 'moment'
import { useEffect, useState } from 'react'
import ReactPlayer from 'react-player'
import { useDebounce } from 'src/hooks/useDebounce'
import { IBlogNewsItem, IBlogParams } from 'src/interfaces/blog'
import { getListBlogAction } from 'src/redux/actions/blog-news'

interface ISelectedBody {
  postId: number
  index?: number
}
interface IProps {
  listSelected: ISelectedBody[]
}
export const useAddPostModal = ({ listSelected }: IProps) => {
  const [listBlogSelected, setListBlogSelected] = useState<ISelectedBody[]>([])
  const dispatch = useAppDispatch()
  const [searchValue, setSearchValue] = useState<string | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const debouncedValue = useDebounce<string | null>(searchValue, 800)
  const { blogs, totalBlogs, pageBlogs, limitBlogs, loadings } = useAppSelector(
    (state: RootState) => state.blog
  )
  const isChangingLibrary = useAppSelector(
    (state) =>
      state.evolveLanding.loadings[thunkActionLoading.ADD_POST_LIBRARY_LOADING]
  )

  const disableRow = (record: IBlogNewsItem) => {
    if (listBlogSelected.length === 8) {
      if (listBlogSelected.some((item) => item.postId === record.id)) {
        return ''
      } else {
        return 'opacity-50'
      }
    }
    return ''
  }

  const onSave = async (action?: () => void) => {
    try {
      if (listBlogSelected?.length === 8) {
        const res = await dispatch(
          addPostToLibraryAction({ posts: listBlogSelected })
        ).unwrap()
        if (res.success) {
          dispatch(getLibraryAction())
          action?.()
        }
      }
    } catch (error: any) {
      if (error.message) {
        message.error({
          content: error.message,
        })
      }
    }
  }

  const getBlogs = async (params?: IBlogParams) => {
    if (params?.status !== '') {
      dispatch(
        getListBlogAction({
          page: +pageBlogs,
          search:
            debouncedValue !== null && debouncedValue.trim()
              ? debouncedValue.trim()
              : undefined,
          ...(params || {}),
        })
      )
    } else {
      dispatch(
        getListBlogAction({
          page: +pageBlogs,
          search:
            debouncedValue !== null && debouncedValue.trim()
              ? debouncedValue.trim()
              : undefined,
          ...(params || {}),
        })
      )
    }
  }

  const getBlogsBySearchValue = async () => {
    try {
      setIsSearching(true)
      await dispatch(
        getListBlogAction({
          page: 1,
          search:
            debouncedValue !== null && debouncedValue.trim()
              ? debouncedValue.trim()
              : undefined,
          status: 1,
        })
      ).unwrap()
    } catch (error: any) {
      if (error.message) {
        message.error({
          content: error.message,
        })
      }
    } finally {
      setIsSearching(false)
    }
  }

  const columns: TableProps<IBlogNewsItem>['columns'] = [
    {
      dataIndex: 'media',
      align: 'center',
      key: 'media',
      width: '4%',
      title: 'Image ',
      render: (_, record) => {
        if (record) {
          if (
            ['image/png', 'image/jpeg', 'image/jpg'].includes(
              record?.thumbnail?.type || ''
            )
          ) {
            return (
              <div className="flex items-center justify-center w-full">
                <img
                  src={record?.thumbnail.url ?? PLACEHOLDER_IMAGE_AVATAR}
                  alt={record?.thumbnail?.url}
                  className="object-cover w-14 h-14"
                />
              </div>
            )
          }

          return (
            <div className="flex items-center justify-center w-full">
              <div className="w-14 h-14">
                <ReactPlayer
                  url={record?.thumbnail?.url}
                  controls={true}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                />
              </div>
            </div>
          )
        } else {
          return (
            <div className="flex items-center justify-center w-full">
              <img
                src={PLACEHOLDER_IMAGE_AVATAR}
                alt={'placeholder'}
                className="object-cover w-14 h-14"
              />
            </div>
          )
        }
      },
    },

    {
      dataIndex: 'title',
      align: 'center',
      key: 'title',
      width: '4%',
      title: 'Title En',
      render: (_, record) => {
        return (
          <Tooltip title={record.title}>
            <p className="truncate ">{record.title}</p>
          </Tooltip>
        )
      },
    },

    {
      dataIndex: 'category',
      align: 'center',
      key: 'category',
      width: '4%',
      title: 'Category',
      render: (_, record) => {
        return <p className="line-clamp-2">{record.type}</p>
      },
    },
    {
      dataIndex: 'date',
      align: 'center',
      key: 'date',
      width: '4%',
      title: 'Date',
      render: (_, record) => {
        return (
          <p className="line-clamp-2">
            {moment(record.postDate).format('MMMM DD, YYYY')}
          </p>
        )
      },
    },

    {
      dataIndex: 'action',
      align: 'center',
      key: 'action',
      width: '4%',
      title: 'Action',
      render: (_, record: IBlogNewsItem) => {
        return (
          <div className="flex justify-center w-full space-x-4">
            <Checkbox
              value={record.id}
              checked={listBlogSelected.some(
                (item) => item.postId === record.id
              )}
              onChange={(val) => {
                if (
                  listBlogSelected.some((item) => item.postId === record.id)
                ) {
                  setListBlogSelected((pre) =>
                    pre.filter((i) => i.postId !== record.id)
                  )
                } else {
                  if (listBlogSelected.length < 8) {
                    const a = listSelected.find(
                      (item) => item.postId === record.id
                    )
                    setListBlogSelected((pre) =>
                      pre.concat({
                        postId: record.id || 0,
                        index: a?.index || undefined,
                      })
                    )
                  }
                }
              }}
            />
          </div>
        )
      },
      ellipsis: true,
    },
  ]

  useEffect(() => {
    setListBlogSelected(listSelected)
  }, [listSelected])

  useEffect(() => {
    const getListBlog = async () => {
      await dispatch(getListBlogAction({ page: 1, status: '1' })).unwrap()
    }
    getListBlog()
  }, [])

  useEffect(() => {
    debouncedValue !== null && getBlogsBySearchValue()
  }, [debouncedValue])

  return {
    blogs,
    totalBlogs,
    pageBlogs,
    limitBlogs,
    columns,
    getBlogs,
    searchValue,
    setSearchValue,
    setIsSearching,
    isSearching,
    onSave,
    isChangingLibrary,
    numberSelected: listBlogSelected.length,
    disableRow,
    isGettingListBlogs: loadings['getListBlogActionLoading'],
  }
}
